import { useState, useMemo, useReducer, useRef } from 'react'
import { MainAppMethods } from '../../App'
import { callNewOrder } from '..//../interop.js';
import { Button, Row, Col, FormControl, Container, Form } from 'react-bootstrap';
import PagerTable from '../../common/tables/PagerTable';

/* New call from Finder File feature
 * --------------------------------------------------------------------------------------------------------------------
 * Note, there are no required arguments however if reactarg1 contains a number... that will be the number of 
 * milliseconds before the lone DNIS (if there is only one DNIS) is automatically navigated to. This is not
 * recommended because the user will have no chance to verify their selection nor choose again. To enabled in OLX
 * update the tab to provide the additional argument in the param string. For example, 3 second auto-select:
 * 
 * update tabs set override_url = '&ReactCmd=ffnewcall&reactarg1=3000'  where tabname = 'enter a new sale'
 * --------------------------------------------------------------------------------------------------------------------* 
 */
 
const FFnewcall = (props: MainAppMethods) => {

  const [refresh, doRefresh] = useReducer((d) => d + 1, 0);
  const [tableData, setTableData] = useState<any>({
    count: 0,
    data: [],
  }) // current data (list of lists) for table.

  // controlled fields
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [zipCode, setZipCode] = useState<string>("")
  const [finderNum, setFinderNum] = useState<string>("")
  const [selection, setSelection] = useState<any>(null)
  const [dnis, setDnis] = useState<string>("")
  const [ani, setAni] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [selectionSummary, setSelectionSummary] = useState<string>("")
  const [fileID, setFileID] = useState<number>(0)
  const [showFinderNumber, setShowFinderNumber] = useState<boolean>(false)
  const inputRef = useRef<any>()
  var autoNavTimer: any = null
  const getColumnsList=()=>{
    var cols = [
      {
        Header: "",
        accessor: 'id', // accessor is the "key" in the data
        maxWidth: 50,
        Cell: (r: any) => <Button size="sm" onClick={(e) => getSourceData(r)} >Go</Button>
      },    
      {
        Header: 'First Name',
        accessor: 'firstName',
        maxWidth: 70
      },
      {
        Header: 'Last Name',
        accessor: "lastName",
        maxWidth: 100
      },
      {
        Header: 'Campaign',
        accessor: "campaign",
        maxWidth: 100
      },
      {
        Header: 'Key Code',
        accessor: "keyCode",
        maxWidth: 60
  
      },
      {
        Header: 'Address',
        accessor: "address"
      },
      {
        Header: 'City',
        accessor: "city",
        maxWidth: 100
      },
      {
        Header: 'State',
        accessor: "state",
        maxWidth: 40
      },
      {
        Header: 'Zip',
        accessor: "zip",
        maxWidth: 70
      },
      {
        Header: 'Mail Date',
        accessor: "mailDropDate",
        maxWidth: 90
      }
    ];
    if(showFinderNumber){
      cols.push(
      {
        Header: 'Finder #',
        accessor: 'finderNumber',
        maxWidth: 70,
      })
    }
    return cols;
  }
  const parseSettings=(resp:any)=>{
    if(resp.settings && resp.settings.optColumns){
      if(resp.settings.optColumns.ffNumber == true){
        setShowFinderNumber(true);
        }
        else{
            setShowFinderNumber(false);
        }         
    } 
    else{
            setShowFinderNumber(false);
        } 
  }
  const columns = useMemo(
    ()=>getColumnsList(),
    [showFinderNumber]
  )


  const getFileData = (pageSize: number, pageIndex: number) => {
    const apiBase = sessionStorage.getItem("apibase");

    // search criteria (b64 encode for GET safety)
    var _firstName = firstName.trim();
    var _lastName = lastName.trim();
    var _zipCode = zipCode.trim();
    var _finderNum = finderNum.trim();

    // if we have no search criteria, shortcut and set empty data for table
    if (_firstName === "" && _lastName === "" && _zipCode === "" && _finderNum === "") {
      setTableData({
        count: 0,
        data: [],
      })
      return;
    }

    var _url = `${apiBase}/v1/FinderFile?FirstName=${_firstName}&LastName=${_lastName}&ZipCode=${_zipCode}&FFNumber=${_finderNum}&pagesize=${pageSize}&pageIndex=${pageIndex}`;

    fetch(_url,
      {
        method: "GET",
        headers: props.getHeaders(),
      })
      .then(response => response.json())
      .then((resp: any) => {
        setTableData(resp);
        parseSettings(resp);
      });
  }

  const getSourceData = (row: any) => {
    // Makes api call to get source details about selected file, then sets selection 
    const apiBase = sessionStorage.getItem("apibase");
    var _fileId = row.row.values.id;
    setFileID(_fileId);

    // create selection summary for the DNIS selection panel 
    const v = row.row.values;
    var summary = `${v.firstName} ${v.lastName},${v.address} ${v.state},${v.zip}`
    setSelectionSummary(summary)

    // https://localhost:44371/api/v1/finderfile/filesources/406
    var _url = `${apiBase}/v1/finderfile/filesources/${_fileId}`;

    fetch(_url,
      {
        method: "GET",
        headers: props.getHeaders(),
      })
      .then(response => response.json())
      .then((resp: any) => {

        // only one source returned... select it. 
        if (resp.data.length === 1) setDnis(resp.data[0].dnis)   //setDefaultDNIS(resp.data[0].dnis);
        setSelection(resp);

        // auto-nav-after X milliseconds in args. 
        if (!isNaN(parseInt(props.args.arg1)) && resp.data.length === 1) {
          // not working with actual DNIS at time of execution.
          // SEE : https://felixgerschau.com/react-hooks-settimeout/
          //autoNavTimer = window.setTimeout(() => doCallNewOrder(), parseInt(props.args.arg1));
          autoNavTimer = window.setTimeout(() => {           
            inputRef?.current?.click();                        
          }, parseInt(props.args.arg1));

        }

      });
  }

  const doCallNewOrder = () => {
    // Call callNewOrder in interop.js, but first make sure things are selected. 
    if (!dnis) {
      setError("Please select a DNIS first.")
      return
    }

    // We need to get the Employee ID too... was returned in the souce data. Doesn't matter which record. 
    var employeeId = selection.data[0].employeeId;

    // reset interface because SIM sources will not cause navigation away from the page. 
    setTableData({
      count: 0,
      data: [],
    })
    setDnis("")
    setError("")
    setSelection(null)
    setFirstName("")
    setLastName("")
    setZipCode("")

    
    // Call function to open call entry page and pass required details. 
    callNewOrder(dnis, ani, employeeId, fileID);
  }


  return <Container>

    {!selection ?
      <>
        <Row>
          <Col>First Name<FormControl type="search" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Col>
          <Col>Last Name<FormControl type="search" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Col>
          <Col>Zip Code<FormControl type="search" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
          </Col>
          <Col>Finder #<FormControl type="search" value={finderNum} onChange={(e) => setFinderNum(e.target.value)} />
          </Col>
        </Row>
        <Row>
          <Col lg={2}>
            <Button variant="primary" onClick={doRefresh} className="mt-1" >Lookup</Button>
          </Col>
          <Col lg={10} />
        </Row>

        <div style={{ "fontSize": "12px" }} >
          {refresh ? <PagerTable getData={getFileData} columns={columns} tableData={tableData} refresh={refresh} showPager={true} /> : null}
        </div>

      </>
      :
      <div>
        <Row className="mb-3">
          <Col lg={12}>
            You are about to enter a call for:   <span style={{ "fontWeight": "bold" }} >{selectionSummary}</span>
          </Col>
        </Row>

        <Row>
          <Col lg={3}>
            <Row>
              <Col lg={2}>DNIS</Col>
              <Col>
                <Form.Group>
                  <Form.Select aria-label="Default select example" onChange={(e) => { setError(""); setDnis(e.target.value) }} defaultValue={dnis} >
                    <option value="">Choose DNIS</option>
                    {// render DNIS's from selection
                      !selection ? null :
                        selection.data.map((s: any) => {
                          return <option key={s.sourceId} value={s.dnis}>{s.dnis}</option>
                        })
                    }
                  </Form.Select>
                </Form.Group>
                {!error ? null : <span style={{ "color": "red" }} >{error}</span>}
              </Col>
            </Row>
          </Col>
          <Col lg={3}>
            <Row>
              <Col lg={2} className="float-end">ANI</Col>
              <Col>
                <Form.Group>
                  <FormControl value={ani} onChange={(e) => setAni(e.target.value)} placeholder="ANI/Phone"></FormControl>
                </Form.Group>
              </Col>
            </Row>
          </Col>
          <Col lg={3}>
            <Button ref={inputRef} variant="primary" className="mt-2" onClick={doCallNewOrder}>GO</Button>
            <Button
              variant="secondary"
              className="float-end mt-2"
              onClick={(e) => { setError(""); setSelection(null); setDnis(""); clearTimeout(autoNavTimer); }}>
              Choose again
            </Button>
          </Col>
        </Row>
      </div>

    }

  </Container>

}

export default FFnewcall; 